import { z } from 'zod';

// =====================================================
// INPUT VALIDATION SCHEMAS
// Centralized validation schemas for all service operations
// =====================================================

// UUID validation
const uuidSchema = z.string().uuid('Invalid UUID format');

// Date validation (YYYY-MM-DD format)
const dateStringSchema = z.string().regex(
  /^\d{4}-\d{2}-\d{2}$/,
  'Date must be in YYYY-MM-DD format'
);

// =====================================================
// BUDGET ENTRY VALIDATION
// =====================================================

export const BudgetEntryInsertSchema = z.object({
  project_id: uuidSchema.optional(),
  site_office_id: uuidSchema.optional(),
  category: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
  notes: z.string().max(2000).optional(),
  date: dateStringSchema.optional().nullable(),
  quantity: z.number().min(0).max(1e9).optional().nullable(),
  unit: z.string().max(50).optional(),
  unit_price: z.number().min(0).max(1e12).optional().nullable(),
  actual_amount: z.number().min(0).max(1e12).optional().nullable(),
  total_amount: z.number().min(0).max(1e12).optional().nullable(),
  tax_percentage: z.number().min(0).max(100).optional().nullable(),
  tax_amount: z.number().min(0).max(1e12).optional().nullable(),
  attachment_url: z.string().url().max(2000).optional().nullable(),
  is_verified: z.boolean().optional(),
  user_id: uuidSchema.optional().nullable(),
});

export const BudgetEntryUpdateSchema = BudgetEntryInsertSchema.partial();

// =====================================================
// BUDGET RAB ITEM VALIDATION
// =====================================================

export const BudgetRABItemSchema = z.object({
  project_id: uuidSchema.optional(),
  category_id: uuidSchema.optional().nullable(),
  name: z.string().min(1, 'Name is required').max(200),
  item_name: z.string().min(1).max(200).optional(),
  description: z.string().max(1000).optional().nullable(),
  unit: z.string().max(50).optional().nullable(),
  quantity: z.number().min(0).max(1e9).optional().nullable(),
  unit_price: z.number().min(0).max(1e12).optional().nullable(),
  total_price: z.number().min(0).max(1e12).optional().nullable(),
});

// =====================================================
// TEAM MEMBER VALIDATION
// =====================================================

export const TeamMemberCreateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  email: z.string().email('Invalid email format').max(255),
  position: z.string().max(100).optional(),
  role: z.enum(['viewer', 'editor', 'manager', 'administrator', 'project-editor', 'document-budget-editor', 'safety', 'site-admin', 'management']).default('viewer'),
  department: z.string().max(100).optional(),
  phone: z.string().max(30).optional().refine(
    (val) => !val || /^[\+]?[0-9\s\-\(\)]{0,30}$/.test(val),
    'Invalid phone number format'
  ),
  status: z.enum(['active', 'inactive']).optional(),
  joinDate: z.string().optional(),
  projectsAssigned: z.number().min(0).max(1000).optional(),
  skills: z.array(z.string().max(50)).max(50).optional(),
  experience: z.string().max(500).optional(),
  reportTo: z.string().max(200).optional().nullable(),
  imageUrl: z.string().or(z.literal('')).optional().nullable(),
  siteOfficeId: uuidSchema.optional().nullable(),
});

export const TeamMemberUpdateSchema = TeamMemberCreateSchema.partial();

// =====================================================
// CHAT MESSAGE VALIDATION
// =====================================================

export const ChatMessageSchema = z.object({
  text: z.string()
    .min(1, 'Message cannot be empty')
    .max(5000, 'Message too long (max 5000 characters)'),
  sender_id: uuidSchema,
  site_office_id: uuidSchema,
  type: z.enum(['text', 'file', 'image']).default('text'),
});

// =====================================================
// PROJECT VALIDATION
// =====================================================

export const ProjectSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  description: z.string().max(2000).optional().nullable(),
  project_number: z.string().max(50).optional().nullable(),
  status: z.enum(['pending', 'in_progress', 'completed', 'on_hold']).optional(),
  supervisor: z.string().max(200).optional(),
  pic: z.string().max(200).optional().nullable(),
  client: z.string().max(200).optional().nullable(),
  location: z.string().max(500).optional().nullable(),
  project_type: z.string().max(100).optional().nullable(),
  start_date: dateStringSchema.optional().nullable(),
  end_date: dateStringSchema.optional().nullable(),
  budget_rab: z.number().min(0).max(1e15).optional().nullable(),
  progress: z.number().min(0).max(100).optional().nullable(),
  site_office_id: uuidSchema.optional().nullable(),
});

// =====================================================
// VALIDATION HELPER FUNCTIONS
// =====================================================

export class ValidationError extends Error {
  constructor(
    message: string,
    public errors: z.ZodError['errors']
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Validate data against a schema and return validated data or throw ValidationError
 */
export function validateData<T extends z.ZodSchema>(
  schema: T,
  data: unknown
): z.infer<T> {
  const result = schema.safeParse(data);
  if (!result.success) {
    const errorMessages = result.error.errors
      .map(e => `${e.path.join('.')}: ${e.message}`)
      .join(', ');
    throw new ValidationError(
      `Validation failed: ${errorMessages}`,
      result.error.errors
    );
  }
  return result.data;
}

/**
 * Safely validate data and return result without throwing
 */
export function safeValidate<T extends z.ZodSchema>(
  schema: T,
  data: unknown
): { success: true; data: z.infer<T> } | { success: false; error: string } {
  const result = schema.safeParse(data);
  if (!result.success) {
    const errorMessages = result.error.errors
      .map(e => `${e.path.join('.')}: ${e.message}`)
      .join(', ');
    return { success: false, error: errorMessages };
  }
  return { success: true, data: result.data };
}

// Type exports for external use
export type BudgetEntryInsert = z.infer<typeof BudgetEntryInsertSchema>;
export type BudgetRABItem = z.infer<typeof BudgetRABItemSchema>;
export type TeamMemberCreate = z.infer<typeof TeamMemberCreateSchema>;
export type ChatMessageInput = z.infer<typeof ChatMessageSchema>;
export type ProjectInput = z.infer<typeof ProjectSchema>;
